
import CryptoJsWordArray from 'crypto-js/lib-typedarrays';
import CryptoJsMd5 from 'crypto-js/md5';
import { Component, Vue, Watch } from 'vue-property-decorator';
import UxLogs, { AccelerometerStatus, MediaData } from '@/api/ux_logs';
import CameraPreview, { getCurrentCameraPreview } from '@/components/CameraPreview.vue';
import { ShootType } from '@/store/modules/camera';
import { SensorData } from '@/store/modules/sensorMotion';
import { getSensorAcc, getSensorAig, getSensorRot } from '@/util/sensorMotion';

@Component
export default class Parts extends Vue {
  timerWait: number = 200;
  timerId: number = -1;
  canvas: HTMLCanvasElement | null = null;
  context: CanvasRenderingContext2D | null = null;
  flagFirst: boolean = true;
  triggerShoot: boolean = false;
  triggerConfirmStart: boolean = false;
  triggerConfirmButton: boolean = false;

  debug1: number[][] = [];
  debug2: [number, number, number, string][] = [];

  @Watch('triggerCountShoot')
  handleTriggerCountShoot() {
    this.triggerShoot = true;
  }

  @Watch('triggerCountConfirmStart')
  handleTriggerCountConfirmStart() {
    this.triggerConfirmStart = true;
  }

  @Watch('triggerCountConfirmButton')
  handleTriggerCountConfirmButton() {
    this.triggerConfirmButton = true;
  }

  @Watch('isStop')
  handleStop() {
    if (this.isStop) return;
    window.clearTimeout(this.timerId);
    this.timerId = window.setTimeout(this.handleTimeout, this.timerWait);
  }

  get triggerCountShoot(): boolean {
    return this.$store.state.sensorMotion.triggerCountShoot;
  }

  get triggerCountConfirmStart(): boolean {
    return this.$store.state.sensorMotion.triggerCountConfirmStart;
  }

  get triggerCountConfirmButton(): boolean {
    return this.$store.state.sensorMotion.triggerCountConfirmButton;
  }

  get isStop(): boolean {
    return this.$store.state.camera.showCameraModeModal;
  }

  mounted(): void {
    this.$store.dispatch('sensorMotion/start');
    this.timerId = window.setTimeout(this.handleTimeout, this.timerWait);
  }

  beforeDestroy(): void {
    window.clearTimeout(this.timerId);
    this.$store.dispatch('sensorMotion/stop');
  }

  async createHash(): Promise<string | null> {
    const blob: Blob | null = await new Promise<Blob | null>(
      (resolve: BlobCallback, reject: (reason?: any) => void): void => {
        const preview: CameraPreview | null = getCurrentCameraPreview();
        if (preview === null) return resolve(null);
        const video: HTMLVideoElement = preview.video;
        const jpegQuarity: number = 0.9;

        const w1: number = video.videoWidth;
        const h1: number = video.videoHeight;
        if (!(w1 > 0 && h1 > 0)) return resolve(null);
        const aspect: number = w1 / h1;
        const w2: number = 160 * Math.min(1, aspect);
        const h2: number = 160 * Math.min(1, 1 / aspect);

        if (this.canvas === null || this.canvas.width !== w2 || this.canvas.height !== h2) {
          const canvas: HTMLCanvasElement = document.createElement('canvas');
          canvas.width = w2;
          canvas.height = h2;
          this.canvas = canvas;
          this.context = canvas.getContext('2d');
        }

        if (this.context === null) return reject(new Error('failed to get context'));
        this.context.drawImage(video, 0, 0, w1, h1, 0, 0, w2, h2);
        this.canvas.toBlob(resolve, 'image/jpeg', jpegQuarity);
      }
    );

    if (blob === null) return null;

    const arrayBuffer: ArrayBuffer = await new Promise<ArrayBuffer>(
      (resolve: (value: ArrayBuffer) => void, reject: (reason?: any) => void): void => {
        if (blob.arrayBuffer) {
          // blob.arrayBufferはiOSのsafariだと14.5から
          blob.arrayBuffer().then(resolve).catch(reject);
        } else {
          const reader: FileReader = new FileReader();
          reader.addEventListener('load', (): void => resolve(reader.result as ArrayBuffer));
          reader.addEventListener('error', (): void => reject(reader.error));
          reader.readAsArrayBuffer(blob);
        }
      }
    );

    const wordArray: CryptoJsWordArray = CryptoJsWordArray.create(arrayBuffer);
    return CryptoJsMd5(wordArray).toString();
  }

  handleTimeout(): void {
    if (this.isStop) return;

    const event: number = this.checkEvent();
    const status: number = this.checkStatus();
    if (event > 0 && status > 0) {
      const timeStart: number = Date.now();
      this.createHash().then((hash: string | null): void => {
        const timeEnd: number = Date.now();

        if (hash !== null) {
          if (this.flagFirst) this.flagFirst = false;
          this.$store.dispatch('sensorMotion/push', { hash, event, status });

          // 確認画面のOK押したタイミングならセンサーログを送信
          if (status === 9) {
            const sensorData: SensorData[] = this.$store.getters['sensorMotion/stack'];
            const accelerometerStatus: AccelerometerStatus = this.$store.getters['sensorMotion/status'];
            const mediaData: MediaData[] = this.$store.getters['cameraMediaData/mediaData'];
            this.$store.commit('sensorMotion/clearSensorData');
            if (sensorData.length > 0) {
              UxLogs.fetch(sensorData, accelerometerStatus, mediaData).catch((error: unknown): void => {});
            }
          }

          // デバッグ用にデータを保持しておく
          if (process.env.NODE_ENV === 'development') {
            this.debug1 = [getSensorAcc(), getSensorAig(), getSensorRot()];
            this.debug2.unshift([event, status, timeEnd - timeStart, hash]);
            if (this.debug2.length > 30) this.debug2.length = 30;
          }
        }

        const timeout: number = Math.max(10, this.timerWait - (timeEnd - timeStart));
        this.timerId = window.setTimeout(this.handleTimeout, timeout);
      });
    } else {
      this.timerId = window.setTimeout(this.handleTimeout, this.timerWait);
    }
  }

  checkEvent(): number {
    const shootType: ShootType = this.$store.state.camera.shootType;
    if (shootType === 'identity-document-front') return 1;
    if (shootType === 'identity-document-diagonal') return 2;
    if (shootType === 'identity-document-back') return 3;
    if (shootType === 'face-front') return 4;
    if (shootType === 'face-action') return 5;
    return -1;
  }

  checkStatus(): number {
    if (this.flagFirst) {
      // 開始
      return 1;
    } else if (this.triggerShoot) {
      this.triggerShoot = false;
      // 手動撮影時のシャッターボタン
      return 4;
    } else if (this.triggerConfirmStart) {
      this.triggerConfirmStart = false;
      // 確認画面スタート
      return 7;
    } else if (this.triggerConfirmButton) {
      this.triggerConfirmButton = false;
      // 確認画面のOK押したタイミング
      return 9;
    } else {
      const routeName: string = this.$route.name ?? '';
      const shootType: ShootType = this.$store.state.camera.shootType;
      if (routeName === this.$store.getters['camera/locationIntroduction'].name) {
        // 説明画面表示中
        return 2;
      } else if (routeName === this.$store.getters['camera/locationShoot'].name) {
        if (shootType === 'face-action') {
          // 首振り撮影
          return 3;
        } else if (!this.$store.state.camera.isDetectStart) {
          if (!this.$store.state.camera.isShooted) {
            // 位置合わせ
            return 3;
          } else {
            // 判定待ち
            return 6;
          }
        } else {
          if (!this.$store.state.camera.isDetectSuccess) {
            // 自動撮影
            return 5;
          } else {
            // 判定待ち
            return 6;
          }
        }
      } else if (routeName === this.$store.getters['camera/locationConfirmation'].name) {
        // 操作待ち
        return 8;
      } else {
        return -1;
      }
    }
  }
}
